import { createPortal } from "react-dom";
import ConfirmModal from "../../../../../components/confirmViewModal/ConfirmModal";
import { Form } from "../../../../../components/ui/common/form";
import TransportDrawerLayout, {
  TransportDrawerLoading
} from "../externalTransport/components/TransportDrawerLayout";
import InternalTransportFreightDistanceSection from "./components/InternalTransportFreightDistanceSection";
import InternalTransportIdentificationSection from "./components/InternalTransportIdentificationSection";
import InternalTransportNotesSection from "./components/InternalTransportNotesSection";
import {
  InternalTransportGate,
  InternalTransportModalData,
  InternalTransportToolVariant
} from "./internalTransport.types";
import { useInternalTransportBaseScreen } from "./hooks/useInternalTransportBaseScreen";

interface InternalTransportBaseScreenProps {
  modalData: InternalTransportModalData;
  gate: InternalTransportGate;
  variant?: InternalTransportToolVariant;
}

const InternalTransportBaseScreen = ({
  modalData,
  gate,
  variant = "mapping"
}: InternalTransportBaseScreenProps) => {
  const screen = useInternalTransportBaseScreen({ modalData, gate, variant });

  if (screen.isPageLoading) {
    return <TransportDrawerLoading />;
  }

  const simulateComparePanel =
    screen.isSimulateCompare && screen.originalSnapshot ? (
      <div className="tw-mb-4 tw-grid tw-grid-cols-1 tw-gap-3 tw-rounded-lg tw-border tw-border-border tw-bg-secondary/20 tw-p-4 tw-text-sm md:tw-grid-cols-2">
        <div>
          <p className="tw-text-xs tw-text-muted-foreground">Original name</p>
          <p>{screen.originalSnapshot.transportName || "—"}</p>
        </div>
        <div>
          <p className="tw-text-xs tw-text-muted-foreground">Original distance (km)</p>
          <p className="tw-font-mono">{screen.originalSnapshot.avgDistance || "—"}</p>
        </div>
        <div>
          <p className="tw-text-xs tw-text-muted-foreground">Original type</p>
          <p>{screen.originalSnapshot.transportType || "—"}</p>
        </div>
        <div>
          <p className="tw-text-xs tw-text-muted-foreground">Original weight</p>
          <p className="tw-font-mono">
            {screen.originalSnapshot.weight} {screen.originalSnapshot.weightUnit}
          </p>
        </div>
      </div>
    ) : null;

  return (
    <>
      <Form {...screen.form}>
        <TransportDrawerLayout
          formId="internal-transport-form"
          onSubmit={screen.handleSubmit(screen.onSubmit)}
          saveDisabled={screen.saveDisabled}
          saveTitle={screen.saveTitle}
          deleteDisabled={screen.deleteDisabled}
          onDeleteClick={() => screen.setVisibleDeleteModal(true)}
          topContent={simulateComparePanel}
        >
          <div className="tw-space-y-6">
            <section className="tw-space-y-3">
              <p className="tw-text-[11px] tw-font-semibold tw-uppercase tw-tracking-wider tw-text-muted-foreground">
                Identification
              </p>
              <InternalTransportIdentificationSection
                control={screen.control}
                disabled={screen.isFormDisabled}
                transportationTypes={screen.transportationTypes}
                typesLoading={screen.transportationTypesFetching}
                typesError={screen.transportationTypesError}
                onRetryTypes={() => {
                  void screen.refetchTransportationTypes();
                }}
                nameDisabled={screen.nameDisabled}
              />
            </section>

            <section className="tw-space-y-3">
              <p className="tw-text-[11px] tw-font-semibold tw-uppercase tw-tracking-wider tw-text-muted-foreground">
                Freight & distance
              </p>
              <InternalTransportFreightDistanceSection
                control={screen.control}
                disabled={screen.isFormDisabled}
              />
            </section>

            {screen.supportsNotes && (
              <InternalTransportNotesSection
                notesRef={screen.notesRef}
                parentId={screen.internalTransportId}
                disabled={screen.isFormDisabled}
              />
            )}
          </div>
        </TransportDrawerLayout>
      </Form>

      {screen.visibleDeleteModal &&
        createPortal(
          <ConfirmModal
            viewModal={screen.visibleDeleteModal}
            closeModal={() => screen.setVisibleDeleteModal(false)}
            cancel={() => screen.setVisibleDeleteModal(false)}
            approve={screen.handleDelete}
            title={screen.deleteTitle}
            subTitle={screen.deleteSubtitle}
          />,
          document.body
        )}
    </>
  );
};

export default InternalTransportBaseScreen;
